import { siteConfig } from '@/config/site'

/**
 * Questions and answers for the five bespoke product pages.
 *
 * Every answer is assembled from claims the corresponding page already makes —
 * products.ts copy, the module and stage lists, the console and access-model
 * sections, and the implementation/support pages linked from them. Nothing here
 * introduces a figure, a certification or a customer count that is not already
 * somewhere in this repository.
 *
 * The same array feeds the rendered <details> block and the FAQPage JSON-LD, so
 * the markup can never describe questions the page does not show.
 */
export type ProductFaqItem = {
  question: string
  /** Plain text. Copied verbatim into `acceptedAnswer.text`. */
  answer: string
  /** Rendered under the answer. Not part of the schema text. */
  links?: readonly { href: string; label: string }[]
}

/**
 * Standalone FAQPage document for `items`, anchored to the page that renders
 * them. It ships as its own <script> from the FAQ section rather than joining
 * the page's SoftwareApplication node, which is why it carries its own
 * @context — same arrangement <Breadcrumbs> uses for BreadcrumbList.
 *
 * `siteConfig.url` is used rather than absoluteUrl(): the latter reads
 * NEXT_PUBLIC_APP_URL, which the deploy sets to the www host that 301s to the
 * canonical non-www origin.
 */
export function faqPageJsonLd(items: readonly ProductFaqItem[], path: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${siteConfig.url}${path}#faq`,
    mainEntity: items.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  }
}

const IMPLEMENTATION_LINKS = [
  {
    href: '/implementation',
    label: 'See the four-week implementation process',
  },
  { href: '/support', label: 'Product support after go-live' },
] as const

export const UDC_FAQS: readonly ProductFaqItem[] = [
  {
    question: 'What is a Unified Digital Campus?',
    answer:
      "Unified Digital Campus is Euclideum's campus ERP platform: the roof over HRMS, Payroll, Accounts, LMS, Cloud and an AI Engine. The modules were never separate systems: they read and write the same records, under one identity and one audit trail, from enquiry through to alumnus.",
  },
  {
    question: 'Which modules ship with Unified Digital Campus?',
    answer:
      'Six, live from day one. HRMS covers the faculty and staff lifecycle, Payroll covers salary, PF, ESI and TDS, Accounts covers fees, ledgers and audit, LMS covers courses, exams and outcomes, Cloud covers hosting, backup and uptime, and the AI Engine covers insight and automation.',
    links: [
      { href: '/products/hrms', label: 'HRMS for schools and colleges' },
      {
        href: '/products/payroll',
        label: 'Payroll software for educational institutions',
      },
      {
        href: '/products/accounts',
        label: 'Accounting and fee governance module',
      },
      {
        href: '/products/lms',
        label: 'Learning management system for colleges',
      },
    ],
  },
  {
    question:
      'How is this different from integrating separate campus ERP modules?',
    answer:
      'There is no connector layer to maintain, because there is nothing to connect. A single fee receipt posts the ledger entry and numbers the receipt in Accounts, releases semester portal access in the LMS, replicates to two regions in Cloud and recalculates the defaulter forecast in the AI Engine, with no manual re-entry and no reconciliation job queued behind it.',
  },
  {
    question:
      'How does role-based access work for a registrar, an HOD, a faculty mentor and a parent?',
    answer:
      'Everyone opens the same student record; the role decides which fields the platform will render. A registrar sees it institution-wide, a head of department sees their own department, a faculty mentor sees assigned mentees and a parent sees their own ward. Redaction happens at the query rather than in the interface, so a restricted field never leaves the server, and granting one is an audit event rather than a settings toggle.',
  },
  {
    question:
      'Can Unified Digital Campus replace the registers and spreadsheets we run today?',
    answer:
      'That is what it is for. Attendance registers, fee counter receipt books, salary sheets in Excel, Tally, biometric device logs, exam mark spreadsheets, library and hostel registers and the accreditation compliance file are each re-keyed on their own schedule. Euclideum migrates what you already run (registers, ledgers, spreadsheets, whatever the last vendor left behind) so the same record is entered once and present everywhere it is needed.',
    links: [
      {
        href: '/migration-service',
        label: 'Talk to us about campus data migration',
      },
    ],
  },
  {
    question: 'What does leadership see without asking anyone to prepare it?',
    answer:
      "A live institutional position: admissions confirmed against sanctioned intake, fee collected for the current semester, attendance posted today, the state of this month's payroll run including PF, ESI and TDS, and NAAC evidence completeness across criteria 1 to 7. Nothing on that page is collated by hand.",
  },
  {
    question: 'What uptime does the platform run at?',
    answer:
      '99.9% system uptime, measured across the production estate rather than stated as a target, on multi-region replication. Cloud (hosting, backup and uptime) is one of the six modules.',
  },
  {
    question:
      'Is this suitable for a school as well as a college or university?',
    answer:
      'Euclideum builds campus ERP software for schools, colleges and universities. If you are evaluating for a school, the school ERP page covers the same ground (admissions, student records, attendance, examinations, fees, HRMS, payroll and LMS) in school terms.',
    links: [
      {
        href: '/schoolerp',
        label: 'Campus ERP software for schools and colleges',
      },
    ],
  },
  {
    question:
      'How long does implementation take, and what happens after go-live?',
    answer:
      'Euclideum runs a four-week implementation: discovery and planning, setup and configuration, migration and testing, then training and launch, with support available after launch.',
    links: IMPLEMENTATION_LINKS,
  },
]

export const HRMS_FAQS: readonly ProductFaqItem[] = [
  {
    question: 'What does an HRMS do for a school or college?',
    answer:
      "HRMS stands for Human Resource Management System. Euclideum's HRMS streamlines and centralizes the entire employee lifecycle for teaching and non-teaching staff (recruitment, onboarding, attendance, performance evaluation and compliance), turning manual HR tasks into automated workflows and giving leadership structured insights.",
  },
  {
    question: 'Which stages of the employee lifecycle are covered?',
    answer:
      'Six, in order: Recruitment for end-to-end talent acquisition, Onboarding for automated employee integration, Attendance for real-time time tracking, Performance for goal setting and reviews, Compliance for policy and regulatory tracking, and Analytics for workforce insights and reporting.',
  },
  {
    question: 'How is staff attendance and leave handled?',
    answer:
      'The leave and attendance engine tracks in real time, with shift scheduling, biometric integration and policy enforcement, so attendance, leave and shift data are captured with zero manual entry.',
  },
  {
    question: 'Does the HRMS feed payroll, or do we re-enter everything?',
    answer:
      'It feeds payroll. Euclideum Payroll is integrated with HR and attendance to eliminate duplication, and both are modules of the same Unified Digital Campus platform, reading and writing the same records under one audit trail.',
    links: [
      {
        href: '/products/payroll',
        label: 'Payroll software for schools and colleges',
      },
      {
        href: '/products/udc',
        label: 'How the modules share one datastore',
      },
    ],
  },
  {
    question: 'How does appraisal and performance tracking work?',
    answer:
      '360° reviews, OKR tracking and continuous feedback loops that align individual growth with organisational goals, reported through real-time HR dashboards.',
  },
  {
    question: 'Can staff see and manage their own records?',
    answer:
      'Yes. There is an employee self-service portal with transparent processes, and mobile access for all staff.',
  },
  {
    question: 'How does the HRMS help with compliance?',
    answer:
      'Policy documentation and compliance monitoring are built in, and automated policy enforcement with audit trails keeps the institution compliant with labour laws and regulations. Compliance is one of the six lifecycle stages, not an afterthought.',
  },
  {
    question: 'Where does HRMS sit in the wider Euclideum platform?',
    answer:
      'HRMS is one of the six modules of Unified Digital Campus, alongside Payroll, Accounts, LMS, Cloud and the AI Engine. Schools running the Euclideum school ERP get HRMS inside the same platform as admissions, student records, attendance, examinations, fees, payroll and LMS.',
    links: [
      { href: '/products/udc', label: 'Unified Digital Campus platform' },
      {
        href: '/schoolerp',
        label: 'Campus ERP software for schools and colleges',
      },
    ],
  },
  {
    question: 'How long does HRMS take to roll out, and what support follows?',
    answer:
      'Euclideum runs a four-week implementation: discovery and planning, setup and configuration, migration and testing, then training and launch, with support available after launch.',
    links: IMPLEMENTATION_LINKS,
  },
]

export const PAYROLL_FAQS: readonly ProductFaqItem[] = [
  {
    question:
      'What does Euclideum payroll software do for an educational institution?',
    answer:
      'It automates complex salary structures, statutory requirements and tax calculations so every payroll cycle is seamless and error-free. Salary, deductions, overtime and bonuses are computed with no manual input, and the module is integrated with HR and attendance to eliminate duplication.',
  },
  {
    question: 'Does it handle TDS, PF and ESI?',
    answer:
      'Yes. TDS, PF, ESI and GST handling is built in and stays current with regulatory changes, so statutory rules do not have to be maintained by hand each cycle.',
  },
  {
    question: 'Where does payroll get attendance and employee data from?',
    answer:
      'From HRMS. Payroll is integrated with HR and attendance, so leave, shift and attendance data reach the salary run instead of being re-entered. Both are modules of the same platform.',
    links: [
      {
        href: '/products/hrms',
        label: 'HRMS software for schools and colleges',
      },
      { href: '/products/udc', label: 'One datastore behind every module' },
    ],
  },
  {
    question: 'Are payslips issued digitally?',
    answer:
      'Yes. Branded, detailed payslips are generated and distributed automatically every single cycle, and staff reach their payslips and tax documents through the employee self-service payroll portal.',
  },
  {
    question:
      'Can one payroll run cover more than one institution or trust entity?',
    answer:
      'Yes. Multi-entity payroll is supported, along with bank transfer integration and reconciliation.',
  },
  {
    question: 'What does an auditor get?',
    answer:
      'Audit-ready payroll reports and filings: complete payroll history, statutory filings and bank-reconciled records, with a twelve-month trail available at any time.',
  },
  {
    question: 'What are the stages of a payroll cycle here?',
    answer:
      'Six, repeating every single cycle: Salary Processing for automated salary computation, Tax Management for TDS, PF and ESI, Compliance for statutory compliance automation, Payslips for digital payslip generation, Reporting for audit-ready reports, and Integrations with your bank and HRMS.',
  },
  {
    question: "How does payroll relate to the institution's accounts?",
    answer:
      'Payroll and Accounts are separate modules on the same datastore. Accounts owns the ledgers, AP/AR, bank reconciliation, GST and budgets, and both modules sit under the one identity and one audit trail that Unified Digital Campus provides.',
    links: [
      {
        href: '/products/accounts',
        label: 'Accounting software for schools and colleges',
      },
      { href: '/products/udc', label: 'Unified Digital Campus platform' },
    ],
  },
  {
    question:
      'How long does it take to get payroll live, and what support follows?',
    answer:
      'Euclideum runs a four-week implementation: discovery and planning, setup and configuration, migration and testing, then training and launch, with support available after launch.',
    links: IMPLEMENTATION_LINKS,
  },
]

export const ACCOUNTS_FAQS: readonly ProductFaqItem[] = [
  {
    question: 'What does the accounting management system cover?',
    answer:
      'Automated ledger and journal management, accounts payable and receivable, bank reconciliation, GST and compliance-ready modules, budget monitoring and expense controls, real-time financial dashboards, multi-entity consolidation and an immutable audit trail.',
  },
  {
    question: 'Is it GST compliant?',
    answer:
      'Yes. The GST and compliance modules are built in, aligned with Indian and international accounting standards and tax regulations, which is what keeps the books audit-ready.',
  },
  {
    question: 'How does bank reconciliation work?',
    answer:
      'Through direct bank feed integration with intelligent transaction matching and real-time exception alerts, so transactions are matched in real time rather than chased at month end.',
  },
  {
    question: 'Does it handle student fees?',
    answer:
      'Within Unified Digital Campus the Accounts module owns fees, ledgers and audit. Fee governance sits on the same record as the rest of the student file, and every fee event is posted against that record in sequence, immutably.',
    links: [
      {
        href: '/products/udc',
        label: 'How fees, ledgers and audit sit on one record',
      },
    ],
  },
  {
    question: 'Can we consolidate several institutions or campuses?',
    answer:
      'Yes. Multi-entity consolidation and reporting is part of the module.',
  },
  {
    question: 'How much faster is month-end close?',
    answer:
      'Automated journal entries, AP/AR and reconciliation reduce close time from weeks to days: 80% faster month-end close, closing in two days.',
  },
  {
    question: 'How are budgets controlled?',
    answer:
      'Set budgets, track actuals in real time and receive alerts before overspending occurs, with budget monitoring and expense controls reported alongside real-time financial dashboards.',
  },
  {
    question: 'Is there an audit trail?',
    answer:
      'Yes. An immutable audit trail and compliance reports keep the institution audit-ready, with automated transaction matching, validation and exception handling behind the numbers.',
  },
  {
    question:
      'How does Accounts relate to payroll and the rest of the campus system?',
    answer:
      'Accounts is one of the six modules of Unified Digital Campus, alongside HRMS, Payroll, LMS, Cloud and the AI Engine, all on one datastore with one identity and one audit trail. Schools running the Euclideum school ERP get fees and finance inside the same platform.',
    links: [
      {
        href: '/products/payroll',
        label: 'Payroll software for educational institutions',
      },
      {
        href: '/schoolerp',
        label: 'Campus ERP software for schools and colleges',
      },
    ],
  },
  {
    question:
      'How long does implementation take, and what support follows go-live?',
    answer:
      'Euclideum runs a four-week implementation: discovery and planning, setup and configuration, migration and testing, then training and launch, with support available after launch.',
    links: IMPLEMENTATION_LINKS,
  },
]

export const LMS_FAQS: readonly ProductFaqItem[] = [
  {
    question: 'What is the Euclideum learning management system?',
    answer:
      'An LMS for colleges and schools that centralizes curriculum management, content distribution, assessments and analytics within a secure digital ecosystem, so educational programmes can be delivered, managed and tracked with precision and scalability.',
  },
  {
    question: 'How are courses and curriculum built?',
    answer:
      'With a drag-and-drop course and curriculum builder supporting video, assessments, SCORM and interactive content.',
  },
  {
    question: 'Does the LMS support SCORM and xAPI?',
    answer:
      'Yes. SCORM and xAPI compatibility is part of the platform, and the course builder takes SCORM content directly.',
  },
  {
    question: 'Are assignments graded automatically?',
    answer:
      'Yes. Assignment submission and auto-grading are built in, and the Assessments module handles automated grading and feedback.',
  },
  {
    question: 'How is learner progress tracked?',
    answer:
      'Real-time visibility into learner journeys, completion rates and granular skill gap analysis, with attendance and progress tracking and performance analytics dashboards for the institution.',
  },
  {
    question: 'Does the LMS adapt to individual learners?',
    answer:
      "AI-adaptive learning paths adjust content, pace and difficulty in real time based on each learner's individual performance patterns.",
  },
  {
    question: 'Can students learn on a phone?',
    answer:
      'Yes. Mobile learning works on any device, on a mobile-responsive design that covers the whole platform.',
  },
  {
    question: 'How does the LMS handle certification and compliance training?',
    answer:
      'Automated recertification reminders, audit trails and compliance tracking, with an automated certification engine issuing the certificates.',
  },
  {
    question: 'How does the LMS connect to the rest of the campus system?',
    answer:
      "The LMS is one of the six modules of Unified Digital Campus, alongside HRMS, Payroll, Accounts, Cloud and the AI Engine, all reading and writing the same records: a fee receipt posted in Accounts releases the learner's semester portal access in the LMS. Schools running the Euclideum school ERP get the LMS inside the same platform.",
    links: [
      { href: '/products/udc', label: 'Unified Digital Campus platform' },
      {
        href: '/schoolerp',
        label: 'Campus ERP software for schools and colleges',
      },
    ],
  },
  {
    question:
      'How long does implementation take, and what support follows go-live?',
    answer:
      'Euclideum runs a four-week implementation: discovery and planning, setup and configuration, migration and testing, then training and launch, with support available after launch.',
    links: IMPLEMENTATION_LINKS,
  },
]
